// eslint-disable-next-line no-unused-vars
import React from 'react';
import styled from '@emotion/styled';
import { bool, shape, arrayOf, number, object, string, func } from 'prop-types';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import {
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableHeader,
  TableRow,
  TableBody,
  TableCell,
  DataTableSkeleton,
  InlineNotification,
} from '@carbon/react';
import { ArrowUpNarrowWide, ArrowDownWideNarrow, ArrowDownUp } from 'lucide-react';

import FilterSerialNumbers from 'Containers/Service/FilterSerialNumbers';
import ServicePagination from 'Containers/Service/ServicePagination';

const StyledRMAComponent = styled.div`
  .cds--inline-notification {
    background: #F5F7F9;
    color: #3A3A3A;
    max-width: 100%;
    border: none;
    margin-bottom: 10px;

    &.cds--inline-notification--error {
      background: #FFF2F1;
    }

    p {
      font-size: 14px;
    }

    .cds--inline-notification__close-button .cds--inline-notification__close-icon {
      fill: #3A3A3A;
    }
  }

  .cds--data-table {
    border: 1px solid #e2e2e2;

    th {
      background-color: #f5f7f9;

      .cds--table-header-label button {
        width: 100%;
        cursor: pointer;
        display: flex;
        align-items: center;
        background: transparent;
        border: none;
        gap: 5px;
        font-size: inherit;
        font-weight: inherit;
      }

      svg {
        width: 18px;
        height: 18px;
        stroke: #707070;
        min-width: 18px;
      }
    }

    td {
      background: #fff;

      .address {
        font-weight: 400;
        word-break: break-all;
      }

      button {
        background: transparent;
        border: none;
        cursor: pointer;

        > svg {
          width: 24px;
        }
      }

      span {
        font-weight: 700;
      }
    }

    tbody tr:hover {
      td {
        background: #f5f7f9;
      }

      &.cds--expandable-row td {
        background: #41618A;
      }
    }
  }
  
  .cds--skeleton__placeholder {
    margin-top: 14px;
    height: 42px;
    width: 100%;
  }

  @media screen and (max-width: ${ props => props.theme.device.laptop }) {
    .cds--skeleton.cds--data-table-container {
      overflow: hidden;
      max-width: calc(100vw - 34px);
    }

    .cds--data-table {
      border: none;

      > thead {
        display: none;
      }

      > tbody {
        background: #fff;

        tr {
          border: 1px solid #e2e2e2;
          position: relative;
          display: flex;
          flex-direction: column;
          height: auto;
          margin-top: 20px;

          &:hover {
            background: #fff;
          }

          td {
            border: none;

            &:first-of-type {
              height: 40px;
              background: #F5F7F9;
            }
          }
        }
      }
    }
  }
`;

const StyledTableCell = styled(TableCell)`
  @media screen and (max-width: ${ props => props.theme.device.laptop }) {
    &[data-label="id"] {
      box-shadow: none !important;
      position: absolute;
      background: transparent !important;
      top: 10px;
    }

    &[data-label="manufacturerName"],
    &[data-label="productCode"],
    &[data-label="serialNumber"],
    &[data-label="invoiceNumber"],
    &[data-label="serialNumberDate"],
    &[data-label="serialNumberWarranty"] {    
      display: flex;
      align-items: center;
      min-height: 40px;
      padding-left: 185px;

      &:before {
        content: "${ props => props.name }:";
        position: absolute;
        left: 10px;
        width: 180px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        font-weight: 600;
      }
    }
  } 
`;

const RMAComponent = ({
  serialNumbers,
  manufacturers,
  sortOptions,
  currentSort,
  defaultSortId,
  handleMobileOrderChange,
  pagination,
  loading,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const headerData = [
    {
      key: 'serialNumber',
      header: t('pages.serialNumber'),
    },
    {
      key: 'productCode',
      header: t('pages.productCode'),
    },
    {
      key: 'manufacturerName',
      header: t('pages.manufacturerName'),
    },
    {
      key: 'invoiceNumber',
      header: t('pages.invoiceNumber'),
    },
    {
      key: 'serialNumberDate',
      header: t('pages.serialNumberDate'),
    },
    {
      key: 'serialNumberWarranty',
      header: t('pages.serialNumberWarranty'),
    },
  ];

  const renderTableHeader = header => {
    const mappedSortingParams = {
      serialNumber: 'serial_number',
      productCode: 'product_code',
      manufacturerName: 'manufacturer_name',
      invoiceNumber: 'invoice_number',
      serialNumberDate: 'serial_number_date',
      serialNumberWarranty: 'serial_number_warranty',
    };

    const [orderBy, orderDirection] = currentSort.id.split('-');
    const isSorted = orderBy === mappedSortingParams[header.key];
    const isAsc = orderDirection === 'asc';

    let sortIcon = <ArrowDownUp />;
    let sortDirection = 'asc';

    if (isSorted && currentSort.id !== defaultSortId) {
      sortIcon = isAsc ? <ArrowUpNarrowWide /> : <ArrowDownWideNarrow />;
      sortDirection = isAsc ? 'desc' : 'none';
    }

    if (isSorted && currentSort.id === defaultSortId) {
      sortIcon = <ArrowDownWideNarrow />;
      sortDirection = 'asc';
    }

    return (
      <button type="button" onClick={ () => handleMobileOrderChange(`${ mappedSortingParams[header.key] }-${ sortDirection }`) }>
        <span>{ header.header }</span>
        { sortIcon }
      </button>
    );
  };

  const mapHeaderToLabel = label => t(`pages.${label}`);

  const renderCell = cell => cell.value;

  return (
    <StyledRMAComponent theme={ theme }>
      <FilterSerialNumbers
        manufacturers={ manufacturers }
        sortOptions={ sortOptions }
        currentSort={ currentSort }
        handleMobileOrderChange={ handleMobileOrderChange }
      />
      <InlineNotification
        kind="info"
        title={ <p>{ t('common.important') }<br />{ t('orders.serialNumberInfo') }</p> }
      />
      { loading ? (
        <DataTableSkeleton showToolbar={ false } showHeader={ false } />
      ) : (
        <DataTable rows={ serialNumbers } headers={ headerData }>
          {({ rows, headers, getHeaderProps, getRowProps, getTableProps }) => (
            <TableContainer>
              <Table { ...getTableProps() }>
                <TableHead>
                  <TableRow>
                    {headers.map(header => (
                      <TableHeader
                        { ...getHeaderProps({ header }) }
                        key={header.key}
                      >
                        { renderTableHeader(header) }
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  { serialNumbers.length === 0 && (<TableRow><TableCell colSpan={ headers.length + 1 }>{ t('orders.noResultsFound') }</TableCell></TableRow>) }
                  { rows.map(row => (
                    <TableRow { ...getRowProps({ row }) } key={ row.id }>
                      { row.cells.map(cell => (
                        <StyledTableCell
                          data-label={ cell.info.header }
                          name={ mapHeaderToLabel(cell.info.header) }
                          key={ cell.id }
                        >
                          { renderCell(cell) }
                        </StyledTableCell>
                      )) }
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DataTable>
      )}
      <ServicePagination loading={ loading } { ...pagination } />
    </StyledRMAComponent>
  );
};

RMAComponent.propTypes = {
  serialNumbers: arrayOf(object),
  manufacturers: arrayOf(object),
  defaultSortId: string,
  currentSort: object,
  sortOptions: arrayOf(object),
  handleMobileOrderChange: func,
  pagination: shape({
    currentPage: number,
    recordsPerPage: number,
    totalPages: number,
    totalRecords: number,
  }),
  loading: bool,
};

RMAComponent.displayName = 'RMAComponent';

export default RMAComponent;
